"use client";

import React, { useState, useEffect } from "react";
import { X, Calendar, Clock, Layers, Award, Users, ShieldAlert, CheckCircle, Info, XCircle, BookOpen, Link as LinkIcon, FileText } from "lucide-react";
import { fetchWithAuth, API_BASE } from "@/lib/auth";
import GigApplicationModal from "@/components/gigs/GigApplicationModal";

interface GigModalProps {
    postingId: string;
    onClose: () => void;
    onActionSuccess?: () => void;
    isEligible?: boolean;
}

const GIG_TABS = [
    { key: "what_we_do",     label: "What You'll Do" },
    { key: "why_serve",      label: "Why Serve" },
    { key: "what_to_expect", label: "What to Expect" },
    { key: "right_for_you",  label: "Right for You?" },
    { key: "get_involved",   label: "Get Involved" },
] as const;

type ActiveTabType = typeof GIG_TABS[number]["key"];

const formatJourney = (j: string) => {
    if (!j) return "";
    let clean = j.trim();
    // Replace underscores/hyphens with space
    clean = clean.replace(/[_-]+/g, " ");
    
    const lower = clean.toLowerCase();
    if (lower === "level1candidate" || lower === "level 1 candidate" || lower === "l1 candidate" || lower === "l1_candidate") return "Level 1 Candidate";
    if (lower === "level2candidate" || lower === "level 2 candidate" || lower === "l2 candidate" || lower === "l2_candidate") return "Level 2 Candidate";
    if (lower === "level3candidate" || lower === "level 3 candidate" || lower === "l3 candidate" || lower === "l3_candidate") return "Level 3 Candidate";
    
    if (lower === "l1eligible" || lower === "l1 eligible") return "L1 Eligible";
    if (lower === "l2eligible" || lower === "l2 eligible") return "L2 Eligible";
    if (lower === "l3eligible" || lower === "l3 eligible") return "L3 Eligible";
    if (lower === "l3_cleared" || lower === "l3 cleared") return "L3 Cleared";

    if (lower === "charterholder") return "Charterholder";
    if (lower === "charterholderinactive" || lower === "charterholder inactive") return "Charterholder Inactive";
    if (lower === "affiliatemember" || lower === "affiliate member") return "Affiliate Member";
    if (lower === "affiliatememberinactive" || lower === "affiliate member inactive") return "Affiliate Member Inactive";
    if (lower === "professionalmember" || lower === "professional member") return "Professional Member";
    if (lower === "professionalmemberinactive" || lower === "professional member inactive") return "Professional Member Inactive";
    if (lower === "inactivecandidate" || lower === "inactive candidate") return "Inactive Candidate";
    
    // Split camelcase if any remains
    clean = clean.replace(/([A-Z])/g, " $1").trim();
    
    // Capitalize each word
    return clean
        .split(/\s+/)
        .map(w => w.charAt(0).toUpperCase() + w.slice(1))
        .join(" ");
};

export default function GigModal({ postingId, onClose, onActionSuccess, isEligible = true }: GigModalProps) {
    const [gig, setGig] = useState<any>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [activeTab, setActiveTab] = useState<ActiveTabType>("what_we_do");
    const [isApplyOpen, setIsApplyOpen] = useState(false);

    const hasRequirements = gig ? !!(
        (gig.geographic_scope && gig.geographic_scope !== "Global") ||
        gig.program_journey_scope ||
        gig.membership_journey_scope
    ) : false;

    const fetchDetails = async () => {
        setIsLoading(true);
        try {
            const res = await fetchWithAuth(`${API_BASE}/api/v1/gigs/${postingId}`);
            if (res.ok) {
                const data = await res.json();
                setGig(data);
            }
        } catch (err) {
            console.error("Error fetching gig details:", err);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchDetails();
    }, [postingId]);

    const handleRespondInvitation = async (accept: boolean) => {
        if (!gig?.invitation_id) return;
        try {
            const res = await fetchWithAuth(`${API_BASE}/api/v1/gigs/invitations/${gig.invitation_id}/respond`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ accept })
            });
            if (res.ok) {
                fetchDetails();
                if (onActionSuccess) onActionSuccess();
            }
        } catch (err) {
            console.error("Failed to respond to invitation:", err);
        }
    };

    if (isLoading) {
        return (
            <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-center justify-center font-sans">
                <div className="bg-white rounded-xl p-8 max-w-sm w-full border border-gray-150 shadow-xl text-center flex flex-col items-center gap-4">
                    <div className="w-8 h-8 border-2 border-[#007a8a]/20 border-t-[#007a8a] rounded-full animate-spin" />
                    <p className="text-xs font-bold text-gray-500 uppercase tracking-wider">Retrieving Opportunity Details...</p>
                </div>
            </div>
        );
    }

    if (!gig) return null;

    // Check if it has local/support chapters
    const hasSupport = gig.name.toLowerCase().includes("campus") || 
        gig.owning_entity_type.toLowerCase() === "chapter";
    const supportLabel = "Local chapters";

    const owningGroupLabel = gig.owning_entity_name || 
        (gig.owning_entity_type.charAt(0).toUpperCase() + gig.owning_entity_type.slice(1));

    // Format cadence text
    const cadenceText = gig.cadence ? gig.cadence.replace(/_/g, "-") : "";
    const cleanTimeCommitment = (gig.time_commitment && gig.time_commitment !== "null") ? gig.time_commitment : "Flexible";
    const cadenceLabel = cadenceText 
        ? (cleanTimeCommitment ? `${cadenceText.charAt(0).toUpperCase() + cadenceText.slice(1)} · ${cleanTimeCommitment}` : cadenceText.charAt(0).toUpperCase() + cadenceText.slice(1))
        : cleanTimeCommitment;

    // Eligibility badge styles
    const getEligibilityInfo = () => {
        const progScope = (gig.program_journey_scope || "").toLowerCase();
        const memScope = (gig.membership_journey_scope || "").toLowerCase();
        const hasGeoScope = gig.geographic_scope && gig.geographic_scope !== "null";
        
        if (progScope.includes("charter")) {
            return { label: "CMT charterholders", bg: "bg-[#a9c4bb] text-[#004d57]" };
        }
        if (memScope.includes("member") || memScope.includes("candidate")) {
            return { label: "Members & candidates", bg: "bg-[#c3d6d9]/45 text-white" };
        }
        if (hasGeoScope) {
            return { label: "Members in the region", bg: "bg-[#c3d6d9]/45 text-white" };
        }
        return { label: "Open to all", bg: "bg-white/10 text-white" };
    };

    const elig = getEligibilityInfo();

    return (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-start justify-center p-4 overflow-y-auto font-sans">
            <div className="bg-[#fcfcfc] border border-gray-200 rounded-xl w-full max-w-2xl shadow-2xl flex flex-col overflow-hidden max-h-[90vh] my-10 animate-in fade-in slide-in-from-bottom-2 duration-300">
                {/* Header Spec: Deep Teal Background */}
                <div className="bg-[#004d57] text-white p-6 pb-5 relative flex flex-col gap-3.5 shrink-0">
                    {/* Close Button */}
                    <button 
                        onClick={onClose}
                        className="absolute top-4 right-4 bg-white/10 hover:bg-white/20 border-none text-white w-[30px] h-[30px] rounded-md transition-all flex items-center justify-center cursor-pointer font-bold"
                        aria-label="Close"
                    >
                        ×
                    </button>

                    {/* Group/Support Pills Row */}
                    <div className="flex flex-wrap items-center gap-1.5 pr-8">
                        {gig.owning_entity_type.toLowerCase() !== "staff" && (
                            <span className="font-sans font-bold text-[10px] text-[#004d57] bg-[#a9c4bb] px-2.5 py-1 rounded-full uppercase tracking-[0.07em]">
                                {owningGroupLabel}
                            </span>
                        )}
                        {hasSupport && (
                            <span className="font-sans font-bold text-[10px] text-white bg-white/20 px-2.5 py-1 rounded-full uppercase tracking-[0.07em]">
                                + {supportLabel}
                            </span>
                        )}
                    </div>

                    {/* Title */}
                    <h2 className="font-sans font-black text-2xl text-white leading-tight">
                        {gig.name}
                    </h2>

                    {/* Badges Row underneath the Title */}
                    <div className="flex flex-wrap gap-2">
                        <span className="inline-flex items-center gap-1.5 text-xs bg-white/10 text-white px-3 py-1 rounded-full whitespace-nowrap">
                            <span className="w-1.5 h-1.5 rounded-full bg-[#a9c4bb] shrink-0" />
                            {cadenceLabel}
                        </span>
                        <span className={`inline-flex items-center text-xs font-semibold ${elig.bg} px-3 py-1 rounded-full whitespace-nowrap`}>
                            {elig.label}
                        </span>
                    </div>
                </div>

                {/* Tab Strip */}
                <div className="border-b border-gray-200 bg-white px-6 flex overflow-x-auto gap-4 no-scrollbar shrink-0">
                    {GIG_TABS.map((tab) => (
                        <button
                            key={tab.key}
                            onClick={() => setActiveTab(tab.key)}
                            className={`py-3.5 text-xs font-bold uppercase tracking-wider whitespace-nowrap border-b-[3px] transition-all shrink-0 cursor-pointer ${
                                activeTab === tab.key
                                    ? "border-[#007a8a] text-[#007a8a]"
                                    : "border-transparent text-gray-400 hover:text-gray-600"
                            }`}
                        >
                            {tab.label}
                        </button>
                    ))}
                </div>

                {/* Tab Contents */}
                <div className="p-6 overflow-y-auto flex-1 text-slate-700 space-y-5 bg-white">
                    {activeTab === "what_we_do" && (
                        <div className="space-y-4">
                            {gig.description && gig.what_to_do && (
                                <div>
                                    <h4 className="font-sans font-bold text-[11px] uppercase tracking-[0.16em] text-[#007a8a] mb-1.5">Summary</h4>
                                    <p className="text-xs sm:text-sm leading-relaxed text-slate-650 font-medium">
                                        {gig.description}
                                    </p>
                                </div>
                            )}
                            <div>
                                <h4 className="font-sans font-bold text-[11px] uppercase tracking-[0.16em] text-[#007a8a] mb-1.5">What you'll actually do</h4>
                                <div className="text-xs sm:text-sm leading-relaxed text-slate-600 whitespace-pre-wrap bg-gray-50 border border-gray-100 rounded-xl p-4">
                                    {gig.what_to_do || gig.description}
                                </div>
                            </div>
                        </div>
                    )}

                    {activeTab === "why_serve" && (
                        <div className="space-y-4">
                            {gig.why_serve && (
                                <div>
                                    <h4 className="font-sans font-bold text-[11px] uppercase tracking-[0.16em] text-[#007a8a] mb-1.5">Value of Service</h4>
                                    <div className="text-xs sm:text-sm leading-relaxed text-slate-600 whitespace-pre-wrap bg-gray-50 border border-gray-100 rounded-xl p-4 mb-4">
                                        {gig.why_serve}
                                    </div>
                                </div>
                            )}
                            <h4 className="font-sans font-bold text-[11px] uppercase tracking-[0.16em] text-[#007a8a] mb-3">Recognition & Growth</h4>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <div className="bg-white border border-gray-150 rounded-xl p-4 flex items-start gap-3 shadow-sm hover:border-gray-200 transition-colors">
                                    <div className="p-2 rounded-lg bg-amber-50 text-amber-800 shrink-0">
                                        <Award className="w-5 h-5" />
                                    </div>
                                    <div>
                                        <h5 className="text-xs font-bold text-[#004d57] uppercase tracking-wider">Sponsor Recognition</h5>
                                        <p className="text-[11px] text-gray-500 mt-1">
                                            {gig.recognition_tier?.toLowerCase() === "tier2"
                                                ? "This gig qualifies for public directory recognition."
                                                : gig.recognition_tier?.toLowerCase() === "tier1"
                                                ? "This gig is recorded on your private service history."
                                                : "This gig qualifies for standard contribution status."}
                                        </p>
                                    </div>
                                </div>
                                <div className="bg-white border border-gray-150 rounded-xl p-4 flex items-start gap-3 shadow-sm hover:border-gray-200 transition-colors">
                                    <div className="p-2 rounded-lg bg-blue-50 text-[#007a8a] shrink-0">
                                        <Users className="w-5 h-5" />
                                    </div>
                                    <div>
                                        <h5 className="text-xs font-bold text-[#004d57] uppercase tracking-wider">Professional Network</h5>
                                        <p className="text-[11px] text-gray-500 mt-1">
                                            Collaborate with CMT Charterholders, build mentors, and impact the global technical analysis community.
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {activeTab === "what_to_expect" && (
                        <div className="space-y-4">
                            {gig.what_to_expect && (
                                <div>
                                    <h4 className="font-sans font-bold text-[11px] uppercase tracking-[0.16em] text-[#007a8a] mb-1.5">Commitment Brief</h4>
                                    <div className="text-xs sm:text-sm leading-relaxed text-slate-600 whitespace-pre-wrap bg-gray-50 border border-gray-100 rounded-xl p-4 mb-4">
                                        {gig.what_to_expect}
                                    </div>
                                </div>
                            )}
                            <h4 className="font-sans font-bold text-[11px] uppercase tracking-[0.16em] text-[#007a8a] mb-2">The hard specifics</h4>
                            <div className="bg-white border border-gray-150 rounded-xl divide-y divide-gray-100 overflow-hidden shadow-sm">
                                <div className="flex items-center gap-4 p-3.5 text-xs sm:text-sm">
                                    <Layers className="w-4 h-4 text-[#007a8a] shrink-0" />
                                    <span className="w-32 shrink-0 font-bold uppercase tracking-wider text-[10px] text-gray-400">Cadence</span>
                                    <span className="text-slate-600 capitalize text-xs">{gig.cadence ? gig.cadence.replace(/_/g, " ") : "ongoing"}</span>
                                </div>
                                {(gig.starts_at || gig.ends_at) && (
                                    <div className="flex items-center gap-4 p-3.5 text-xs sm:text-sm">
                                        <Calendar className="w-4 h-4 text-[#007a8a] shrink-0" />
                                        <span className="w-32 shrink-0 font-bold uppercase tracking-wider text-[10px] text-gray-400">Term Dates</span>
                                        <span className="text-slate-600">
                                            {gig.starts_at ? new Date(gig.starts_at).toLocaleDateString("en-US", {month:"short", day:"numeric", year:"numeric"}) : "Immediate"} to {gig.ends_at ? new Date(gig.ends_at).toLocaleDateString("en-US", {month:"short", day:"numeric", year:"numeric"}) : "Unspecified"}
                                        </span>
                                    </div>
                                )}
                                {gig.closes_at && (
                                    <div className="flex items-center gap-4 p-3.5 text-xs sm:text-sm">
                                        <Calendar className="w-4 h-4 text-[#007a8a] shrink-0" />
                                        <span className="w-32 shrink-0 font-bold uppercase tracking-wider text-[10px] text-gray-400">Apply By</span>
                                        <span className="text-slate-600">
                                            {new Date(gig.closes_at).toLocaleDateString("en-US", {month:"short", day:"numeric", year:"numeric"})}
                                        </span>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    {activeTab === "right_for_you" && (
                        <div className="space-y-4">
                            {gig.right_for_you && (
                                <div>
                                    <h4 className="font-sans font-bold text-[11px] uppercase tracking-[0.16em] text-[#007a8a] mb-1.5">Suitable Competencies</h4>
                                    <div className="text-xs sm:text-sm leading-relaxed text-slate-600 whitespace-pre-wrap bg-gray-50 border border-gray-100 rounded-xl p-4 mb-4">
                                        {gig.right_for_you}
                                    </div>
                                </div>
                            )}
                            <h4 className="font-sans font-bold text-[11px] uppercase tracking-[0.16em] text-[#007a8a] mb-2">Gate Requirements</h4>
                            <div className="bg-[#003d45]/5 rounded-xl p-4 border border-[#003d45]/10 flex items-start gap-3 shadow-sm">
                                <ShieldAlert className="w-5 h-5 text-[#007a8a] shrink-0 mt-0.5" />
                                <div className="w-full">
                                    <div className="grid grid-cols-1 gap-2 font-sans text-xs">
                                        {gig.geographic_scope && gig.geographic_scope !== "Global" && (
                                            <div className="flex items-center gap-2 bg-white/60 border border-gray-150/40 rounded-lg p-2.5">
                                                <span className="font-black uppercase tracking-widest text-[8px] text-[#007a8a] bg-[#007a8a]/10 px-1.5 py-0.5 rounded shrink-0">Geographic scope</span>
                                                <span className="font-semibold text-slate-700">
                                                    {gig.geographic_scope
                                                        .split(", ")
                                                        .map((s: string) => {
                                                            const clean = s.trim();
                                                            const lower = clean.toLowerCase();
                                                            if (lower === "apac") return "APAC Region";
                                                            if (lower === "amer") return "AMER Region";
                                                            if (lower === "emea") return "EMEA Region";
                                                            if (lower === "latam") return "LATAM Region";
                                                            return clean;
                                                        })
                                                        .join(", ")
                                                    }
                                                </span>
                                            </div>
                                        )}
                                        
                                        {(gig.program_journey_scope || gig.membership_journey_scope) ? (
                                            <>
                                                {gig.program_journey_scope && (
                                                    <div className="flex items-center gap-2 bg-white/60 border border-gray-150/40 rounded-lg p-2.5">
                                                        <span className="font-black uppercase tracking-widest text-[8px] text-[#007a8a] bg-[#007a8a]/10 px-1.5 py-0.5 rounded shrink-0">Candidate journey</span>
                                                        <span className="font-semibold text-slate-700">
                                                            {gig.program_journey_scope.split(", ").map((s: string) => formatJourney(s)).join(", ")}
                                                        </span>
                                                    </div>
                                                )}
                                                {gig.membership_journey_scope && (
                                                    <div className="flex items-center gap-2 bg-white/60 border border-gray-150/40 rounded-lg p-2.5">
                                                        <span className="font-black uppercase tracking-widest text-[8px] text-[#007a8a] bg-[#007a8a]/10 px-1.5 py-0.5 rounded shrink-0">Member journey</span>
                                                        <span className="font-semibold text-slate-700">
                                                            {gig.membership_journey_scope.split(", ").map((s: string) => formatJourney(s)).join(", ")}
                                                        </span>
                                                    </div>
                                                )}
                                            </>
                                        ) : (
                                            <div className="flex items-center gap-2 bg-white/60 border border-gray-150/40 rounded-lg p-2.5">
                                                <span className="font-black uppercase tracking-widest text-[8px] text-[#007a8a] bg-[#007a8a]/10 px-1.5 py-0.5 rounded shrink-0">Designation / role</span>
                                                <span className="font-semibold text-slate-700 text-xs">Open to All</span>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {activeTab === "get_involved" && (
                        <div className="space-y-6">
                            <h4 className="font-sans font-bold text-[11px] uppercase tracking-[0.16em] text-[#007a8a] mb-2">Action Center</h4>
                            
                            {gig.application_status ? (
                                gig.application_status === "rejected" ? (
                                    <div className="bg-rose-50 border border-rose-100 text-rose-800 p-4 rounded-xl flex items-start gap-3">
                                        <XCircle className="w-5 h-5 text-rose-600 shrink-0 mt-0.5" />
                                        <div>
                                            <h5 className="text-xs font-bold uppercase tracking-wider text-rose-800">Application Rejected</h5>
                                            <p className="text-[11px] sm:text-xs text-rose-700 mt-1">
                                                Your application was not accepted for this gig.
                                            </p>
                                            {gig.application_rejection_reason && (
                                                <p className="text-[11px] sm:text-xs text-rose-800 mt-2 bg-rose-100/50 p-2.5 rounded-xl border border-rose-200/50">
                                                    <strong>Reason:</strong> {gig.application_rejection_reason}
                                                </p>
                                            )}
                                        </div>
                                    </div>
                                ) : (
                                    <div className="bg-emerald-50 border border-emerald-100 text-emerald-800 p-4 rounded-xl flex flex-col gap-3">
                                        <div className="flex items-start gap-3">
                                            <CheckCircle className="w-5 h-5 text-emerald-600 shrink-0 mt-0.5" />
                                            <div>
                                                <h5 className="text-xs font-bold uppercase tracking-wider text-emerald-800">
                                                    {gig.application_status === "approved" ? "Application Approved" : "Application Submitted"}
                                                </h5>
                                                <p className="text-[11px] sm:text-xs text-emerald-700 mt-1">
                                                    {gig.application_status === "approved"
                                                        ? "Congratulations! Your application has been approved and you are assigned to this gig."
                                                        : `Your application is currently in ${gig.application_status} status. The group leaders will review it shortly.`}
                                                </p>
                                                {gig.application_status === "approved" && gig.application_approval_message && (
                                                    <p className="text-[11px] sm:text-xs text-emerald-850 mt-2 bg-emerald-100/60 p-2.5 rounded-xl border border-emerald-200/50">
                                                        <strong>Message from Group Leader:</strong> {gig.application_approval_message}
                                                    </p>
                                                )}
                                            </div>
                                        </div>
                                        {gig.application_status === "approved" && gig.orientation_resources && gig.orientation_resources.length > 0 && (
                                            <div className="mt-2 pt-3 border-t border-emerald-100/70 space-y-2">
                                                <h6 className="text-[10px] font-black uppercase tracking-widest text-[#003d45] flex items-center gap-1.5">
                                                    <BookOpen className="w-3.5 h-3.5" />
                                                    Orientation Materials & Links
                                                </h6>
                                                <div className="grid grid-cols-1 gap-2">
                                                    {gig.orientation_resources.map((res: any, idx: number) => {
                                                        const isLink = res.type === "link";
                                                        return (
                                                            <a
                                                                key={idx}
                                                                href={res.url.startsWith("http") ? res.url : `${API_BASE}${res.url}`}
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                                className="flex items-center justify-between p-2.5 rounded-xl border border-emerald-100/50 bg-white hover:bg-emerald-50/50 hover:border-[#007a8a]/20 transition-all duration-300 group no-underline"
                                                            >
                                                                <div className="flex items-center gap-2">
                                                                    {isLink ? (
                                                                        <LinkIcon className="w-3.5 h-3.5 text-[#007a8a]" />
                                                                    ) : (
                                                                        <FileText className="w-3.5 h-3.5 text-[#007a8a]" />
                                                                    )}
                                                                    <span className="text-[11px] font-semibold text-slate-700 truncate max-w-[200px] sm:max-w-[320px]">
                                                                        {res.title || res.name || res.filename || "Untitled Resource"}
                                                                    </span>
                                                                </div>
                                                                <span className="text-[9px] font-bold text-gray-400 uppercase tracking-widest">
                                                                    {isLink ? "Open Link" : res.size ? `${(res.size / 1024).toFixed(1)} KB` : "Download"}
                                                                </span>
                                                            </a>
                                                        );
                                                    })}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                )
                            ) : gig.invitation_status === "invited" ? (
                                <div className="bg-[#007a8a]/5 border border-[#007a8a]/10 p-5 rounded-xl space-y-4">
                                    <div className="flex items-start gap-3">
                                        <Info className="w-5 h-5 text-[#007a8a] shrink-0 mt-0.5" />
                                        <div>
                                            <h5 className="text-xs font-bold text-[#003d45] uppercase tracking-wider">You Have Been Invited</h5>
                                            <p className="text-[11px] sm:text-xs text-gray-600 mt-1">
                                                The group chairs have personally invited you to join this opportunity. Please accept or decline the invitation.
                                            </p>
                                        </div>
                                    </div>
                                    <div className="flex gap-3">
                                        <button
                                            onClick={() => handleRespondInvitation(true)}
                                            className="bg-[#007a8a] hover:bg-[#006977] text-white px-4 py-2.5 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all border-none cursor-pointer"
                                        >
                                            Accept Invitation
                                        </button>
                                        <button
                                            onClick={() => handleRespondInvitation(false)}
                                            className="bg-gray-100 hover:bg-gray-250 text-gray-700 px-4 py-2.5 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all border-none cursor-pointer"
                                        >
                                            Decline
                                        </button>
                                    </div>
                                </div>
                            ) : !isEligible ? (
                                <div className="bg-amber-50 border border-amber-100 p-5 rounded-xl space-y-4">
                                    <div className="flex items-start gap-3">
                                        <ShieldAlert className="w-5 h-5 text-amber-700 shrink-0 mt-0.5" />
                                        <div>
                                            <h5 className="text-xs font-bold text-amber-800 uppercase tracking-wider">Not Eligible to Apply</h5>
                                            <p className="text-[11px] sm:text-xs text-amber-700 mt-1 leading-relaxed">
                                                This gig is shown for your journey roadmap and future planning. You do not currently satisfy the required credentials, membership scope, or geographic region required to register for this opportunity.
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            ) : (
                                <div className="space-y-4">
                                    <p className="text-xs text-gray-500 leading-relaxed">
                                        Ready to contribute your skills? Click below to submit your application details and availability.
                                    </p>
                                    <button
                                        onClick={() => setIsApplyOpen(true)}
                                        className="bg-[#007a8a] hover:bg-[#006977] text-white px-6 py-3 rounded-xl text-xs font-black uppercase tracking-widest transition-all shadow-sm border-none cursor-pointer"
                                    >
                                        Express Interest
                                    </button>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>

            {/* Application Form Modal */}
            {isApplyOpen && (
                <GigApplicationModal
                    postingId={postingId}
                    gigTitle={gig.name}
                    onClose={() => setIsApplyOpen(false)}
                    onSuccess={() => {
                        setIsApplyOpen(false);
                        fetchDetails();
                        if (onActionSuccess) onActionSuccess();
                    }}
                />
            )}
        </div>
    );
}

